// Built-ins
import { useState, useEffect } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";

import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../../global_vars";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import AutocompleteSelect from "../../common/AutocompleteSelect";
import { DOCUMENT_STATUS_CHOICES } from "../../common/documentStatus";
import { ACCEPTED_DOCUMENT_ACCEPT } from "../../common/documentUploadUtils";
import DateInput from "../../common/DateInput";
import RichTextEditor from "../../common/RichTextEditor";
import { htmlForApi } from "../../../utils/richText";
import AddDocumentCategoryModal from "./add_document_category";


// Variables
window.Swal = Swal;

// API endpoints
const ADD_DOCUMENT = "https://ultima.icsgr.com/api/data_management/all_documents/";
const GET_PROJECTS = "https://ultima.icsgr.com/api/data_management/all_projects/";
const GET_CLIENTS = "https://ultima.icsgr.com/api/data_management/all_clients/";
const GET_DOCUMENT_CATEGORIES = "https://ultima.icsgr.com/api/administration/all_document_categories/";

// Helpers
const clampLen = (value, max) => value.slice(0, max);

function AddDocumentModal({
  onClientCreated,
  refreshData,
  defaultProjectId,
  defaultClientId,
  lockProject = false,
  lockClient = false,
  /** When set with lockProject, multi-select only from these clients (project associated clients). */
  projectAssociatedClients,
  defaultCategoryId = "",
}) {
  const [show, setShow] = useState(false);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);

  const [title, setTitle] = useState(""); // required
  const [projectId, setProjectId] = useState(""); // optional - this will store project_id
  const [clientId, setClientId] = useState(""); // optional - this will store client_id
  const [selectedRelatedClientIds, setSelectedRelatedClientIds] = useState([]);
  const [categoryId, setCategoryId] = useState("");

  const useProjectLinkedPicker =
    lockProject && Array.isArray(projectAssociatedClients);
  const [validUntil, setValidUntil] = useState(""); // required
  const [file, setFile] = useState(null); // optional file upload
  const [original, setOriginal] = useState(false); // optional, default false
  const [trafficable, setTrafficable] = useState(true); // optional, default false
  const [status, setStatus] = useState(""); // optional
  const [notes, setNotes] = useState(""); // optional

  // Fetch projects and clients on component mount
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const currentHeaders = {
          ...headers,
          "Authorization": "Token " + localStorage.getItem("userToken")
        };

        // Fetch projects
        const projectsResponse = await axios.get(GET_PROJECTS, { headers: currentHeaders });
        if (projectsResponse.data && projectsResponse.data.all_projects) {
          setProjects(projectsResponse.data.all_projects);
        }

        // Fetch clients
        const clientsResponse = await axios.get(GET_CLIENTS, { headers: currentHeaders });
        if (clientsResponse.data && clientsResponse.data.all_clients) {
          setClients(clientsResponse.data.all_clients);
        }

        const catRes = await axios.get(GET_DOCUMENT_CATEGORIES, { headers: currentHeaders });
        const cats = catRes?.data?.all_document_categories || [];
        setCategories(Array.isArray(cats) ? cats.filter((c) => c.active !== false) : []);
      } catch (error) {
        console.error('Error fetching reference data:', error);
      }
    };

    fetchReferenceData();
  }, []);

  const resetForm = () => {
    setTitle("");
    setProjectId("");
    setClientId("");
    setSelectedRelatedClientIds([]);
    setCategoryId("");
    setValidUntil("");
    setFile(null);
    setOriginal(false);
    setTrafficable(true);
    setStatus("");
    setNotes("");
  };

  const handleClose = () => setShow(false);
  const handleShow = () => {
    resetForm();
    if (defaultProjectId) setProjectId(defaultProjectId);
    if (defaultClientId) setClientId(defaultClientId);
    if (defaultCategoryId) setCategoryId(defaultCategoryId);
    setShow(true);
  };

  const isTitleValid = title.trim().length >= 2 && title.trim().length <= 40;
  const isFormValid = isTitleValid;

  const createNewDocument = async () => {
    try {
      // Update headers with current token
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      // Ensure we don't force JSON content-type; allow multipart boundary
      delete currentHeaders["Content-Type"]; 

      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("validuntil", validUntil);
      if (projectId) formData.append("project_id", projectId);
      if (useProjectLinkedPicker && selectedRelatedClientIds.length > 0) {
        formData.append("related_client_ids", selectedRelatedClientIds.join(","));
      } else if (clientId) {
        formData.append("client_id", clientId);
      }
      if (status) formData.append("status", status);
      if (categoryId) formData.append("category_id", categoryId);
      formData.append("original", original ? "true" : "false");
      formData.append("trafficable", trafficable ? "true" : "false");
      const notesApi = htmlForApi(notes);
      if (notesApi) formData.append("notes", notesApi);
      if (file) formData.append("filepath", file);

      const response = await axios.post(ADD_DOCUMENT, formData, { headers: currentHeaders });

      if (response.status === 201) {
        if (refreshData) refreshData();
        handleClose();
      }
    } catch (error) {
      console.error("Error creating document:", error);

      let errorMessage = "An error occurred while creating the document.";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data) {
        // Handle validation errors
        const errors = error.response.data;
        const errorMessages = Object.entries(errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        errorMessage = errorMessages;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    }
  };

  return (
    <>
      <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
        <BiPlus
          style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }}
        />
        Create new Document
      </Button>

      <GuardedModal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(clampLen(e.target.value, 40))}
                    placeholder="Enter document title"
                    isInvalid={title.length > 0 && !isTitleValid}
                  />
                  <Form.Control.Feedback type="invalid">
                    Title must be 2-40 characters.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <SelectWithInlineCreate
                  className="mb-3"
                  label="Project"
                  value={projectId}
                  onChange={setProjectId}
                  disabled={lockProject}
                  placeholderOption="Select a project (optional)"
                  overviewLinkEntity="project"
                >
                  {projects.map((project) => (
                    <option key={project.project_id} value={project.project_id} title={project.project_id}>
                      {project.title}
                    </option>
                  ))}
                </SelectWithInlineCreate>
              </Col>
              <Col md={6}>
                {useProjectLinkedPicker ? (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Linked clients (optional)
                      {selectedRelatedClientIds.length > 0 ? (
                        <AiOutlineCheckCircle style={{ color: "green", marginLeft: "0.5em" }} />
                      ) : null}
                    </Form.Label>
                    <div
                      style={{
                        maxHeight: 220,
                        overflowY: "auto",
                        border: "1px solid #ced4da",
                        borderRadius: 4,
                        padding: "8px 10px",
                        background: "#fff",
                      }}
                    >
                      {projectAssociatedClients.length === 0 ? (
                        <small className="text-muted">
                          No associated clients on this project yet. Add associations first, or leave empty for a
                          project-only document.
                        </small>
                      ) : (
                        projectAssociatedClients.map((cli) => {
                          const cid = cli.client_id;
                          const label =
                            cli.fullname ||
                            `${cli.surname || ""} ${cli.name || ""}`.trim() ||
                            cid;
                          return (
                            <Form.Check
                              key={cid}
                              type="checkbox"
                              id={`add-doc-client-${cid}`}
                              className="mb-1"
                              title={cid}
                              label={label}
                              checked={selectedRelatedClientIds.includes(cid)}
                              onChange={() => {
                                setSelectedRelatedClientIds((prev) =>
                                  prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]
                                );
                              }}
                            />
                          );
                        })
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      Select one or more clients linked to this project. The first selected is stored as the primary
                      client on the document.
                    </Form.Text>
                  </Form.Group>
                ) : (
                  <SelectWithInlineCreate
                    className="mb-3"
                    label={
                      <>
                        Client
                        {clientId !== "" ? (
                          <AiOutlineCheckCircle style={{ color: "green", marginLeft: "0.5em" }} />
                        ) : null}
                      </>
                    }
                    labelClassName="mb-1 d-inline-flex align-items-center flex-wrap"
                    value={clientId}
                    onChange={setClientId}
                    disabled={lockClient}
                    placeholderOption="Select a client (optional)"
                    overviewLinkEntity="client"
                  >
                    {clients.map((client) => (
                      <option key={client.client_id} value={client.client_id} title={client.client_id}>
                        {client.fullname || `${client.surname || ""} ${client.name || ""}`.trim() || client.client_id}
                      </option>
                    ))}
                  </SelectWithInlineCreate>
                )}
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Valid until</Form.Label>
                  <DateInput
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>File (optional)</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                    accept={ACCEPTED_DOCUMENT_ACCEPT}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <AutocompleteSelect
                    value={status}
                    onChange={setStatus}
                    placeholder="Select status (optional)"
                  >
                    {DOCUMENT_STATUS_CHOICES.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label}
                      </option>
                    ))}
                  </AutocompleteSelect>
                </Form.Group>
              </Col>
              <Col md={6}>
                <SelectWithInlineCreate
                  className="mb-3"
                  label="Category"
                  value={categoryId}
                  onChange={setCategoryId}
                  placeholderOption="Uncategorized (optional)"
                  overviewLinkEntity="document_category"
                  onAddClick={() => setAddCategoryOpen(true)}
                >
                  {categories.map((c) => (
                    <option key={c.doccate_id} value={c.doccate_id} title={c.doccate_id}>
                      {c.title}
                    </option>
                  ))}
                </SelectWithInlineCreate>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <RichTextEditor
                    rows={3}
                    value={notes}
                    onChange={setNotes}
                    placeholder="Enter notes (optional)"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Original"
                    checked={original}
                    onChange={(e) => setOriginal(e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Trafficable"
                    checked={trafficable}
                    onChange={(e) => setTrafficable(e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>

          </Form>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "red" }}
              >
                {!isTitleValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Title is required (2–40 chars).
                  </li>
                )}
              </ul>
            ) : (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "green" }}
              >
                <li>
                  <AiOutlineCheckCircle
                    style={{ fontSize: 18, marginRight: 6 }}
                  />
                  Validated
                </li>
              </ul>
            )}
          </small>

          <Button color="red" onClick={handleClose}>
            Close
          </Button>
          <Button
            color="green"
            onClick={createNewDocument}
            disabled={!isFormValid}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </GuardedModal>
      <AddDocumentCategoryModal
        nested
        hideTrigger
        show={addCategoryOpen}
        onHide={() => setAddCategoryOpen(false)}
        onDocumentCategoryCreated={(created) => {
          if (created?.doccate_id) {
            setCategories((prev) => {
              const next = [...prev.filter((c) => c.doccate_id !== created.doccate_id), created];
              next.sort((a, b) => (a.orderindex ?? 0) - (b.orderindex ?? 0) || String(a.title).localeCompare(String(b.title)));
              return next;
            });
            setCategoryId(created.doccate_id);
          }
          setAddCategoryOpen(false);
        }}
      />
    </>
  );
}

export default AddDocumentModal;
